// src/pages/NotFound.tsx
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled, { keyframes, useTheme } from "styled-components";
import { Compass, House, ArrowLeft } from "@phosphor-icons/react";

type Dir = "rtl" | "ltr";

export default function NotFound() {
  const navigate = useNavigate();
  const { dir } = (useTheme() as { dir?: Dir }) ?? {};
  const lang: "ar" | "en" = dir === "rtl" ? "ar" : "en";

  const T = useMemo(
    () => ({
      title: { ar: "الصفحة غير موجودة", en: "Page not found" }[lang],
      sub: {
        ar: "تعذر العثور على الصفحة المطلوبة.",
        en: "We couldn’t find what you were looking for.",
      }[lang],
      home: { ar: "الصفحة الرئيسية", en: "Go Home" }[lang],
      back: { ar: "رجوع", en: "Go Back" }[lang],
    }),
    [lang]
  );

  return (
    <Wrap>
      <Decor aria-hidden />
      <Card className="card">
        <IconCircle>
          <Compass size={28} weight="duotone" />
        </IconCircle>

        <Big404 aria-label="404">404</Big404>
        <Title>{T.title}</Title>
        <Subtitle>{T.sub}</Subtitle>

        <Buttons>
          <Primary as={Link} to="/">
            <House size={18} weight="bold" />
            <span>{T.home}</span>
          </Primary>
          <Ghost onClick={() => navigate(-1)}>
            <ArrowLeft size={18} weight="bold" />
            <span>{T.back}</span>
          </Ghost>
        </Buttons>
      </Card>
    </Wrap>
  );
}

/* ---------- styles ---------- */
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
  100% { transform: translateY(0px); }
`;

const shine = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const Wrap = styled.main`
  display: grid;
  place-items: center;
  padding: clamp(16px, 4vw, 32px);
  position: relative;
`;

const Decor = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(1px 1px at 20% 30%, #ffffff30, transparent 40%),
    radial-gradient(1px 1px at 60% 70%, #ffffff26, transparent 40%),
    radial-gradient(1px 1px at 80% 20%, #ffffff20, transparent 40%);
  opacity: 0.35;
`;

const Card = styled.section`
  width: min(680px, 100%);
  padding: clamp(18px, 3.2vw, 28px);
  border-radius: 16px;
  background: var(--surface);
  box-shadow: var(--shadow);
  text-align: center;
  animation: ${float} 6s ease-in-out infinite;
`;

const IconCircle = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 26px;
  display: grid;
  place-items: center;
  margin: 0 auto 8px;
  color: #fff;
  background: linear-gradient(135deg, var(--primary), #16a34a);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
`;

const Big404 = styled.div`
  font-family: var(--rubik-font), ui-sans-serif, system-ui, Arial, sans-serif;
  font-weight: 800;
  font-size: clamp(56px, 14vw, 120px);
  line-height: 0.9;
  letter-spacing: 2px;
  margin: 6px 0 10px;
  background: linear-gradient(90deg, var(--primary), #22c55e, var(--primary));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${shine} 5s linear infinite;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.25rem, 2.6vw, 1.8rem);
  color: var(--text);
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 8px 0 18px;
  color: var(--muted);
  font-size: clamp(0.95rem, 2vw, 1.05rem);
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const BaseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 120ms ease, filter 120ms ease, opacity 120ms ease;
  &:hover { transform: translateY(-2px); filter: brightness(1.02); }
`;

const Primary = styled(BaseBtn)`
  background: var(--primary);
  color: #fff;
  border: none;
`;

const Ghost = styled(BaseBtn)`
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
`;
